/** @jsxImportSource @emotion/react */

'use client';
import { Paper, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import * as styles from './styles';

interface Props {
  children: ReactNode;

  head?: ReactNode | string;
  cta?: ReactNode;
  flex?: number;
  sx?: SxProps<Theme>;
  noOverflowHidden?: boolean;
}

export default function SectionCard({ head, cta, flex, sx, children, noOverflowHidden }: Props) {
  return (
    <Paper
      elevation={0}
      css={styles.paper}
      sx={{
        flex: flex ? `${flex} ${flex} auto` : '',
        padding: 0,
        backgroundColor: 'transparent',
        overflow: noOverflowHidden ? 'initial' : 'hidden',
        ...sx,
      }}
    >
      <Stack
        css={styles.content}
        sx={{
          gap: 2.5,
          flexGrow: 1,
          overflow: noOverflowHidden ? 'initial' : 'hidden',
        }}
      >
        <Head
          head={head}
          cta={cta}
        />
        <Stack sx={{ flex: '1 1 auto', px: 2 }}>{children}</Stack>
      </Stack>
    </Paper>
  );
}

interface HeadProps {
  head?: ReactNode | string;
  cta?: ReactNode;
}

function Head({ head, cta }: HeadProps) {
  if (!head && !cta) {
    return null;
  }

  if (head && typeof head === 'string') {
    return (
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 3.5,
          px: 2,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: 24,
            lineHeight: '32px',
          }}
        >
          {head}
        </Typography>
        {cta}
      </Stack>
    );
  }

  if (head && cta) {
    throw new Error('SectionCard: title and cta cannot be used together');
  }

  return head || null;
}
